import type { HourlyBucket } from '../../types/dashboard'
import { hourLabel } from '../../lib/dashboardHelpers'
import { Bar } from 'react-chartjs-2'

import type { WidgetSettings } from '../../stores/dashboardLayoutStore'
import { useChartColors } from '../../hooks/useChartColors'
import { useThemeStore } from '../../stores/themeStore'

export default function HourlyChart({ hourly, settings, channel = 'all' }: { hourly: HourlyBucket[], settings?: WidgetSettings, channel?: string }) {
    const labels = hourly.map(h => hourLabel(h.hour))
    const showDiscounts = settings?.showDiscounts !== false
    const comp = settings?.chartCompareTo || 'lastWeek'
    const colors = useChartColors()
    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const textColor = colorMode === 'dark' ? '#94a3b8' : '#475569'
    const gridColor = colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const datasets: any[] = [
        {
            label: 'Net Sales',
            data: hourly.map(h => h.sales - h.discount),
            backgroundColor: colors.primary,
            borderRadius: 4, stack: 'today', order: 2,
        }
    ]

    if (showDiscounts) {
        datasets.push({
            label: 'Discounts',
            data: hourly.map(h => h.discount),
            backgroundColor: colors.primarySubtle,
            borderRadius: 4, stack: 'today', order: 2,
        })
    }

    if (comp !== 'none') {
        let compData
        let compLabel
        if (comp === 'yesterday') {
            compData = hourly.map(h => h.yesterdaySales)
            compLabel = 'Yesterday'
        } else if (comp === 'lastMonth') {
            compData = hourly.map(h => h.lastMonthSales)
            compLabel = 'Last Month'
        } else if (comp === 'lastYear') {
            compData = hourly.map(h => h.lastYearSales)
            compLabel = 'Last Year'
        } else {
            compData = hourly.map(h => h.lastWeekSales)
            compLabel = 'Last Week'
        }

        datasets.push({
            type: 'line' as const,
            label: compLabel,
            data: compData,
            borderColor: comp === 'lastYear' ? colors.comp2 : colors.comp1,
            borderDash: comp === 'lastYear' ? [2, 2] : [4, 4], borderWidth: 2,
            pointRadius: 0, pointHoverRadius: 4,
            fill: false, order: 1,
        })
    }

    const data = { labels, datasets }

    return (
        <div className="chart-panel">
            <div className="chart-panel-title">Sales by Hour{channel === 'instore' ? ' - In-Store' : channel === 'online' ? ' - Online' : ' - All Channels'}</div>
            <div className="chart-panel-content">
                <Bar data={data as any} options={{
                    responsive: true, maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { position: 'bottom', labels: { usePointStyle: true, pointStyle: 'rect', boxWidth: 10, color: textColor, font: { size: 12 } } },
                        tooltip: {
                            backgroundColor: 'rgba(15, 23, 42, 0.95)', titleFont: { size: 12 }, bodyFont: { size: 11 },
                            callbacks: { label: (ctx) => `${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toLocaleString()}` }
                        }
                    },
                    scales: {
                        x: { grid: { display: false }, ticks: { color: textColor, font: { size: 11 } } },
                        y: {
                            stacked: true, grid: { color: gridColor },
                            ticks: { color: textColor, font: { size: 11 }, callback: (v) => `$${Number(v).toLocaleString()}` }
                        }
                    }
                }} />
            </div>
        </div>
    )
}
